/**
 * Utilitários para formatação de datas baseado no projeto Angular original
 */

/**
 * Converte um valor de data em timestamp (ms), aceitando Date, string,
 * ou um Timestamp do Firestore (objeto com toMillis()/seconds). Útil para
 * ordenar listas cujos campos de data podem vir direto do Firestore sem
 * conversão prévia.
 * @returns Milissegundos desde epoch, ou 0 se o valor não puder ser interpretado
 */
export const toMillisFlexible = (value: unknown): number => {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'object') {
    const maybeTimestamp = value as { toMillis?: () => number; seconds?: number };
    if (typeof maybeTimestamp.toMillis === 'function') return maybeTimestamp.toMillis();
    if (typeof maybeTimestamp.seconds === 'number') return maybeTimestamp.seconds * 1000;
  }
  const parsed = new Date(value as string).getTime();
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Converte uma string de data em formato brasileiro (DD/MM/YYYY) para objeto Date
 * @param dateString String da data no formato DD/MM/YYYY ou YYYY-MM-DD
 * @returns Objeto Date
 */
export const parseDate = (dateString: string): Date => {
  if (!dateString) return new Date();
  
  // Se a data já está em formato ISO (YYYY-MM-DD), usar diretamente
  if (dateString.includes('-') && dateString.length === 10) {
    return new Date(dateString + 'T00:00:00');
  }
  
  // Se a data está em formato brasileiro (DD/MM/YYYY)
  const parts = dateString.split('/');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Mês começa em 0
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  
  // Fallback para outros formatos
  return new Date(dateString);
};

/**
 * Formata uma data para o formato brasileiro DD/MM/YYYY
 * @param dateString String da data ou objeto Date
 * @returns String formatada DD/MM/YYYY
 */
export const formatDate = (dateString: string | Date): string => {
  if (!dateString) return '';
  
  let date: Date;
  if (typeof dateString === 'string') {
    date = parseDate(dateString);
  } else {
    date = dateString;
  }
  
  // Verificar se a data é válida
  if (isNaN(date.getTime())) {
    return '';
  }
  
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Formata uma data para exibição completa
 * @param dateString String da data
 * @returns String formatada com dia da semana
 */
export const formatDateLong = (dateString: string): string => {
  const date = parseDate(dateString);
  
  if (isNaN(date.getTime())) {
    return '';
  }
  
  return date.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Calcula o tempo decorrido desde uma data
 * @param dateString String da data
 * @returns String com tempo decorrido (ex: "2 meses atrás")
 */
export const getTimeAgo = (dateString: string): string => {
  const date = parseDate(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return 'Hoje';
  } else if (diffDays === 1) {
    return 'Ontem';
  } else if (diffDays < 30) {
    return `${diffDays} dias atrás`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} ${months === 1 ? 'mês' : 'meses'} atrás`;
  } else {
    const years = Math.floor(diffDays / 365);
    return `${years} ${years === 1 ? 'ano' : 'anos'} atrás`;
  }
};

/**
 * Verifica se uma data é recente (últimos 3 meses)
 * @param dateString String da data
 * @returns Boolean indicando se é recente
 */
export const isRecentDate = (dateString: string): boolean => {
  const date = parseDate(dateString);
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  return date >= threeMonthsAgo;
};

/**
 * Converte uma data para o formato ISO (YYYY-MM-DD) para inputs HTML
 * @param dateString String da data em formato brasileiro
 * @returns String no formato ISO
 */
export const toISOString = (dateString: string): string => {
  if (!dateString) return '';
  
  const date = parseDate(dateString);
  if (isNaN(date.getTime())) {
    return '';
  }
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Converte uma data do formato ISO (YYYY-MM-DD) para formato brasileiro
 * @param isoString String da data em formato ISO
 * @returns String no formato brasileiro DD/MM/YYYY
 */
export const fromISOString = (isoString: string): string => {
  if (!isoString) return '';
  
  const date = new Date(isoString + 'T00:00:00');
  if (isNaN(date.getTime())) {
    return '';
  }
  
  return formatDate(date);
};
